import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Article } from '../../../lib/content';
import { formatViews, getCategoryByKey } from '../../../lib/content';

interface HeroCarouselProps {
  articles: Article[];
}

const hotKeywords = ['城市更新', '人工智能', '秋日养生', '国潮'];

export default function HeroCarousel({ articles }: HeroCarouselProps) {
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState('');

  useEffect(() => {
    if (articles.length <= 1) return;
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % articles.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [articles.length]);

  if (articles.length === 0) return null;
  const current = articles[active];
  const category = getCategoryByKey(current.category);

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
  };

  return (
    <section className="relative w-full h-[460px] md:h-[580px] overflow-hidden bg-primary-950">
      {articles.map((article, index) => (
        <div
          key={article.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === active ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - WWW.17C.COM`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/25" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20" />

      <div className="relative h-full w-full px-4 md:px-8 flex items-center">
        <div className="max-w-2xl">
          <div className="flex items-center gap-3 mb-4">
            {category && (
              <span className="text-xs font-medium text-white bg-accent-500 px-2.5 py-1 rounded-md">
                {category.name}
              </span>
            )}
            <span className="text-xs text-white/70">头条 · 焦点推荐</span>
          </div>

          <h1 className="font-heading font-bold text-2xl md:text-4xl text-white leading-tight line-clamp-2">
            {current.title}
          </h1>
          <p className="mt-4 text-sm md:text-base text-white/80 leading-relaxed line-clamp-2 max-w-xl">
            {current.summary}
          </p>

          <div className="mt-5 flex items-center gap-4 text-xs text-white/70">
            <span className="flex items-center gap-1">
              <i className="ri-time-line" />
              {current.publishedAt}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-fire-line" />
              {formatViews(current.views)} 阅读
            </span>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${current.id}`}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-accent-500 text-white text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap"
            >
              阅读全文
              <i className="ri-arrow-right-line" />
            </Link>
            <Link
              to="/special"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-white/10 text-white text-sm font-medium border border-white/25 hover:bg-white/20 transition-colors whitespace-nowrap"
            >
              浏览专题
            </Link>
          </div>

          <form onSubmit={handleSearch} className="mt-7 max-w-md">
            <div className="flex items-center bg-white/95 rounded-full p-1 pl-4">
              <i className="ri-search-line text-foreground-400" />
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="输入关键词，搜索资讯、话题、专题..."
                className="flex-1 bg-transparent px-3 h-10 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
              />
              <button
                type="submit"
                className="h-10 px-5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                搜索
              </button>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-white/70">
              <span>热搜：</span>
              {hotKeywords.map((kw) => (
                <Link
                  key={kw}
                  to={`/search?q=${encodeURIComponent(kw)}`}
                  className="px-2 py-0.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                >
                  {kw}
                </Link>
              ))}
            </div>
          </form>
        </div>
      </div>

      <div className="absolute bottom-6 right-4 md:right-8 flex items-center gap-3">
        <button
          type="button"
          onClick={() => setActive((prev) => (prev - 1 + articles.length) % articles.length)}
          className="w-9 h-9 rounded-full bg-white/15 text-white flex items-center justify-center hover:bg-white/30 transition-colors cursor-pointer"
          aria-label="上一条"
        >
          <i className="ri-arrow-left-s-line text-lg" />
        </button>
        <button
          type="button"
          onClick={() => setActive((prev) => (prev + 1) % articles.length)}
          className="w-9 h-9 rounded-full bg-white/15 text-white flex items-center justify-center hover:bg-white/30 transition-colors cursor-pointer"
          aria-label="下一条"
        >
          <i className="ri-arrow-right-s-line text-lg" />
        </button>
      </div>

      <div className="absolute bottom-7 left-4 md:left-8 flex items-center gap-2">
        {articles.map((article, index) => (
          <button
            key={article.id}
            type="button"
            onClick={() => setActive(index)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              index === active ? 'w-8 bg-accent-500' : 'w-3 bg-white/40 hover:bg-white/70'
            }`}
            aria-label={`切换到第 ${index + 1} 条`}
          />
        ))}
      </div>
    </section>
  );
}